import { getAuth } from "firebase/auth";
import { doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import Flag from "react-world-flags";
import SpeechBubble from "../components/icebreaker/SpeechBubble";
import PublicProfileModal from "../modals/publicProfileModal";
import { generateQuestion } from "../utilities/generateQuestion";

const db = getFirestore();

export default function IceBreakerPage() {
	const { uid } = useParams();
	const auth = getAuth();
	const loggedInUser = auth.currentUser;
	const [currentUser, setCurrentUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [showProfileModal, setShowProfileModal] = useState(false);
	const openProfileModal = () => setShowProfileModal(true);
	const closeProfileModal = () => setShowProfileModal(false);

	useEffect(() => {
		if (!loggedInUser || loggedInUser.uid !== uid) return;

		const fetchUser = async () => {
			try {
				const userDoc = await getDoc(doc(db, "users", uid));
				if (!userDoc.exists()) return;

				const data = userDoc.data();

				setCurrentUser({
					uid,
					...data,
					// ✅ Default language
					language: data.language || "en",
					subject: data.subject || "userData",
				});
			} catch (err) {
				console.error("Failed to load user:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchUser();
	}, [uid, loggedInUser]);

	const onGenerateQuestion = useCallback((userData) => generateQuestion(userData), []);

	const saveLanguage = async (uid, lang) => {
		try {
			await updateDoc(doc(db, "users", uid), {
				language: lang,
			});
		} catch (err) {
			console.error("Failed to save language:", err);
		}
	};

	const handleSubjectChange = useCallback(async (uid, subjectChosen) => {
		console.log("Subject chosen:", subjectChosen);

		try {
			await updateDoc(doc(db, "users", uid), {
				subject: subjectChosen,
			});
			console.log("Subject saved:", subjectChosen);
		} catch (err) {
			console.error("Failed to update subject:", err);
		}
	}, []);

	// 🔒 Block access if not owner
	if (!loggedInUser) {
		return <Navigate to="/" replace />;
	}

	if (loggedInUser.uid !== uid) {
		return <Navigate to="/" replace />;
	}

	if (loading) {
		return <div className="text-center mt-5">Loading...</div>;
	}

	return (
		<div className="mt-4">
			<div className="w-100 text-center">
				<h3 className="w-100 text-center">
					{currentUser?.nationality && (
						<Flag code={currentUser.nationality.code} style={{ width: "30px", height: "20px" }} className="me-2" />
					)}

					<span onClick={openProfileModal} className="profile-link" style={{ cursor: "pointer" }}>
						{currentUser ? currentUser.displayName : ""}
					</span>
				</h3>
			</div>

			<div className="d-flex justify-content-center my-4">
				{currentUser?.photoURL ? (
					<img
						src={currentUser.photoURL}
						alt={currentUser.displayName}
						style={{
							maxHeight: "70vh",
							maxWidth: "70vw",
							objectFit: "contain",
							borderRadius: "10px",
						}}
					/>
				) : (
					<p>No photo available</p>
				)}
			</div>

			{currentUser && (
				<SpeechBubble
					data={currentUser}
					initialLanguage={currentUser.language}
					initialSubject={currentUser.subject}
					onGenerateQuestion={onGenerateQuestion}
					onLanguageChange={(lang) => saveLanguage(currentUser.uid, lang)}
					onSubjectChange={(subjectChosen) => handleSubjectChange(currentUser.uid, subjectChosen)}
				/>
			)}

			{/* Fullscreen Profile Modal */}
			<PublicProfileModal show={showProfileModal} onHide={closeProfileModal} currentUser={currentUser} />
		</div>
	);
}
